import { useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { MessageCircle, Mail, Globe, Send, Loader2, MousePointer2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_URL = "https://wa.me/525516836714";

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor completa nombre, correo y mensaje.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/leads`, form);
      if (res.data.email_sent) {
        toast.success("¡Mensaje enviado! Nuestro equipo fue notificado por correo y le responderá muy pronto.");
      } else {
        toast.warning("Su solicitud fue registrada, pero la notificación por correo falló. También puede contactarnos por WhatsApp.");
      }
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("No pudimos enviar tu mensaje. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-ecnavy/15 bg-white px-5 py-3.5 text-sm text-ecdeep placeholder:text-slate-400 focus:outline-none focus:border-ecamber focus:ring-2 focus:ring-ecamber/25 transition-colors duration-200";

  return (
    <section id="contacto" className="relative py-24 md:py-36 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          num="11"
          chapter="Contacto"
          id="contacto"
          title={<>Hablemos de su próximo <span className="text-ecamber">proyecto</span></>}
          sub="Cotizaciones rápidas, atención directa y seguimiento comercial desde el primer contacto."
        />

        <div className="mt-16 grid lg:grid-cols-[1fr_1.15fr] gap-8 items-start">
          <div className="space-y-6">
            <Reveal>
              <div className="rounded-3xl bg-ecnavy p-10 text-white" data-testid="contact-advisor-card">
                <div className="flex items-center gap-5">
                  <span className="h-16 w-16 rounded-2xl bg-ecamber text-ecdeep flex items-center justify-center">
                    <MousePointer2 size={28} strokeWidth={2.2} fill="#0B1D3A" />
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-semibold">Asesor especializado</h3>
                    <p className="text-sm text-white/70 mt-0.5">eClick México</p>
                  </div>
                </div>
                <div className="mt-8 space-y-3">
                  <a
                    href={WA_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 rounded-xl bg-white/[0.07] border border-white/10 px-5 py-4 hover:bg-white/[0.12] hover:border-ecamber/50 transition-colors duration-300"
                    data-testid="contact-whatsapp-link"
                  >
                    <MessageCircle size={19} className="text-ecamber shrink-0" />
                    <div>
                      <p className="text-xs text-white/50 uppercase tracking-wider">WhatsApp</p>
                      <p className="text-sm font-medium">+52 55 1683 6714</p>
                    </div>
                  </a>
                  <a
                    href="mailto:soluciones@eclick.mx"
                    className="flex items-center gap-4 rounded-xl bg-white/[0.07] border border-white/10 px-5 py-4 hover:bg-white/[0.12] hover:border-ecamber/50 transition-colors duration-300"
                    data-testid="contact-email-link"
                  >
                    <Mail size={19} className="text-ecamber shrink-0" />
                    <div>
                      <p className="text-xs text-white/50 uppercase tracking-wider">Correo</p>
                      <p className="text-sm font-medium">soluciones@eclick.mx</p>
                    </div>
                  </a>
                  <div
                    className="flex items-center gap-4 rounded-xl bg-white/[0.07] border border-white/10 px-5 py-4"
                    data-testid="contact-website-info"
                  >
                    <Globe size={19} className="text-ecamber shrink-0" />
                    <div>
                      <p className="text-xs text-white/50 uppercase tracking-wider">Sitio web</p>
                      <p className="text-sm font-medium">Próximamente nuestro sitio web: eclickgo.com</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="rounded-3xl border border-ecnavy/10 bg-ecmist p-8 flex items-center gap-7 soft-shadow" data-testid="contact-qr-card">
                <div className="bg-white rounded-2xl p-4 border border-ecnavy/10 shrink-0">
                  <QRCodeSVG value={WA_URL} size={116} fgColor="#16305C" bgColor="#FFFFFF" level="M" />
                </div>
                <div>
                  <h4 className="font-display text-base font-semibold text-ecdeep">Escanee para contactar</h4>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">
                    Comuníquese directamente por WhatsApp con eClick México para recibir atención personalizada.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-3xl border border-ecnavy/10 bg-white p-8 md:p-10 soft-shadow" data-testid="contact-form">
              <h3 className="font-display text-xl font-semibold text-ecdeep mb-8">Solicite una cotización</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <input className={inputCls} placeholder="Nombre completo *" value={form.name} onChange={set("name")} data-testid="contact-input-name" />
                <input className={inputCls} placeholder="Empresa" value={form.company} onChange={set("company")} data-testid="contact-input-company" />
                <input className={inputCls} type="email" placeholder="Correo electrónico *" value={form.email} onChange={set("email")} data-testid="contact-input-email" />
                <input className={inputCls} placeholder="Teléfono" value={form.phone} onChange={set("phone")} data-testid="contact-input-phone" />
              </div>
              <textarea
                className={`${inputCls} mt-5 min-h-[140px] resize-none`}
                placeholder="Cuéntenos sobre su requerimiento: volúmenes, categorías, plazos… *"
                value={form.message}
                onChange={set("message")}
                data-testid="contact-input-message"
              />
              <button
                type="submit"
                disabled={sending}
                className="mt-7 w-full rounded-full bg-ecnavy text-white font-semibold py-4 text-sm hover:bg-ecdeep transition-colors duration-300 shadow-[0_16px_40px_rgba(22,48,92,0.25)] flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="contact-submit-button"
              >
                {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} className="text-ecamber" />}
                {sending ? "Enviando…" : "Enviar solicitud"}
              </button>
              <p className="mt-4 text-xs text-slate-400 text-center">Respuesta en menos de 24 horas hábiles.</p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
